import { useAppDispatch, useAppSelector } from "../../../redux/store";
import React, { useEffect, useState } from "react";
import { getMyEveryRecipient } from "../../../redux/slices/recipients.slice";
import { CustomerPageEmpty, MonthlyLimitsContainer } from "./styled";
import { Message, MessageSections } from "../../components/message/message";
import { Recipient } from "../../shared/models/user/recipient/recipient";
import { MonthlyLimit } from "../../shared/models/monthly-limit";
import { getCurrencies, getWeightUnits } from "../../../redux/slices/static-data.slice";
import { Grid, styled } from "@mui/material";
import BarekamText from "../../components/text";
import recipientService, { RecipientUpdateMeta } from "../../../services/recipient.service";
import { ActionBarWithSaveButton } from "../settings/user-actionbar/action-bar-with-save-button";
import { toast, ToastContainer } from "react-toastify";
import { StandardDialog } from "../../components/dialogs/standard-dialog/standard-dialog";
import { LimitedRecipientCard } from "./limited-recipient-card/limited-recipient-card";
import { EditRecipientLimits } from "./edit-recipient-limits/edit-recipient-limits";
import Joi from "joi";
import { ActionBar } from "../../components/action-bar/action-bar";
import { getMessage } from "../../shared/helper-functions";

const BarekamTextWrapper = styled(BarekamText)`
    @media screen and (max-width: 545px) {
        white-space: normal !important;
    }
`;

export const MonthlyLimits = () => {
    const [isReady, setIsReady] = useState<boolean>(false);

    const [popupOpened, setPopupOpened] = useState(false);
    const [recipientToEdit, setRecipientToEdit] = useState<Recipient | undefined>();
    const [limitedRecipients, setLimitedRecipients] = useState<Recipient[]>([]);

    const { allRecipients, isLoading } = useAppSelector(state => state.recipientDataReducer);

    const { messageCache, languageId } = useAppSelector(state => state.messagesReducer);

    const { currencies } = useAppSelector(
          (state) => state.staticDataReducer,
        );

    const dispatch = useAppDispatch();

    useEffect(() => {
        if (isReady) {
            setLimitedRecipients(allRecipients.filter(recipient => !!recipient.monthlyLimit && recipient?.monthlyLimit?.priceLimitPerParcel))
        }
    }, [allRecipients])

    useEffect(() => {
        Promise.allSettled([
            dispatch(getMyEveryRecipient()),
            dispatch(getWeightUnits()),
            dispatch(getCurrencies())
        ]).then(() => {
            setLimitedRecipients(allRecipients.filter(recipient => !!recipient.monthlyLimit && recipient?.monthlyLimit?.priceLimitPerParcel))

            setIsReady(true)
        })
    }, [isReady]);

    const PrimaryActionText = () => (
        <>
            <BarekamText variant={"xxl"} width={'unset'}>
                +
            </BarekamText>

            <BarekamTextWrapper variant={'m'} bold>
                <Message messageKey={'add_new_monthly_limits'} sectionId={MessageSections.USER} />
            </BarekamTextWrapper>
        </>
    )

    function onRemove(recipient: Recipient) {
        editRecipient(recipient, null);
    }

    function handleMonthlyLimitSet(recipient: Recipient, monthlyLimitData: MonthlyLimit) {
        const finalLimit: { [key: string]: any } = { ...monthlyLimitData };

        finalLimit.currency = finalLimit.priceLimitPerParcel ? currencies[1] : undefined;
        finalLimit.weightUnit = finalLimit.weightLimitPerParcel ? 1 : undefined;

        editRecipient(recipient, finalLimit);
    }

    function editRecipient(recipient: Recipient, monthlyLimit: MonthlyLimit | null) {

        const updateData = monthlyLimit ? {
            ...monthlyLimit,
            weightUnit: monthlyLimit?.weightUnit?.weightUnitId,
            currency: monthlyLimit?.currency?.currencyId,
        } : { ...recipient, monthlyLimit: undefined };

        recipientService.editRecipient(recipient.recipientId, { monthlyLimit: updateData })
            .then((response: RecipientUpdateMeta) => {
                if (response.acknowledged) {
                    dispatch(getMyEveryRecipient());
                    setRecipientToEdit(undefined);
                    setPopupOpened(false);
                }
            })
            .catch(reason => {
                const errorMessageKeys: string[] = reason?.response?.data
                    ? reason.response.data.map((error: Joi.ValidationError) => error.message)
                    : ['something_went_wrong'];

                errorMessageKeys.forEach(messageKey => {
                    toast['error'](
                        getMessage(messageKey, MessageSections.MAIN, messageCache, languageId), {
                        position: 'top-right',
                        autoClose: 3000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                    });
                })
            })
    }

    function setAddNewPopupOpened(recipient?: Recipient) {
        if (recipient) {
            setRecipientToEdit(recipient);
        }

        setPopupOpened(true);
    }

    function handlePopupClose() {
        setRecipientToEdit(undefined);
        setPopupOpened(false);
    }

    return (
        <>
            <ToastContainer />

            <ActionBar headerText={<Message messageKey={'set_monthly_limits'} sectionId={MessageSections.USER} />} />

            <MonthlyLimitsContainer>
                <ActionBarWithSaveButton primaryActionText={<PrimaryActionText />} onPrimaryActionClick={() => setAddNewPopupOpened()} />

                {isLoading
                    ? <></>
                    : limitedRecipients.length ?
                        <Grid container
                            gridTemplateColumns={'repeat(auto-fill, minmax(22rem, 1fr))'}
                            display={'grid'}
                            padding={'1rem'}
                            columnGap={'1rem'}
                            rowGap={'1rem'}>
                            {limitedRecipients.map(recipient =>
                                <LimitedRecipientCard recipient={recipient}
                                    onClick={() => setAddNewPopupOpened(recipient)}
                                    onRemove={() => onRemove(recipient)}
                                    key={recipient.recipientId} />
                            )}
                        </Grid>
                        : <CustomerPageEmpty>
                            <BarekamText variant={"xxl"} align={"center"} bold opaque>
                                <Message messageKey={'no_monthly_limits'} sectionId={MessageSections.USER} />
                            </BarekamText>
                        </CustomerPageEmpty>
                }

                <StandardDialog
                    headerLabel={<Message messageKey={'add_new_monthly_limits'} sectionId={MessageSections.USER} />}
                    open={popupOpened}>
                    <EditRecipientLimits sx={{ boxShadow: 'unset' }}
                        recipientToEdit={recipientToEdit as Recipient || {}}
                        limitSaved={handleMonthlyLimitSet}
                        popupClosed={() => handlePopupClose()} />
                </StandardDialog>
            </MonthlyLimitsContainer>
        </>
    )
}
